import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../firebaseConfig';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { addTask, fetchTasks, updateTask } from '../pages/Clients/Common/Functions';
import Toast from './Toast';
import GradientSubmitButton from './GradientSubmitButton';

// Update interface to match the actual data structure from fetchTasks
interface TaskData {
    id: string;
    task: string;
    dueDate: Timestamp;
    isDone: boolean;
    createdAt: Timestamp;
    userId: string;
}

interface AddTaskRequest {
    leadId: string;
    task: string;
    dueDate: Timestamp;
}

interface TasksProps {
    write: boolean;
}

const Tasks = ({ write }: TasksProps) => {
    const [tasks, setTasks] = useState<TaskData[]>([]);
    const [showAddTask, setShowAddTask] = useState(false);
    const [newTask, setNewTask] = useState("");
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [newDueDate, setNewDueDate] = useState("");
    const { leadId } = useParams<{ leadId: string }>();

    useEffect(() => {
        console.log('useEffect triggered - fetching tasks');
        fetchTasksList();
    }, [leadId]);

    const fetchTasksList = async () => {
        try {
            if (!leadId) return;
            console.log('Fetching tasks for leadId:', leadId);
            const response = await fetchTasks(db, auth.currentUser, { leadId });
            console.log('Tasks fetched:', response.data.tasks);
            const formattedTasks: TaskData[] = response.data.tasks.map((task: any) => ({
                id: task.id,
                task: task.task,
                dueDate: task.dueDate,
                isDone: task.isDone,
                createdAt: task.createdAt,
                userId: task.userId
            }));
            setTasks(formattedTasks);
        } catch (error) {
            console.error('Error fetching tasks:', error);
        }
    };

    const handleAddTask = async () => {
        if (!newTask.trim()) {
            return;
        }
        if (!newDueDate) {
            return;
        }
        if (!leadId) {
            return;
        }
        if (!auth.currentUser) {
            return;
        }

        try {
            const dueDate = Timestamp.fromDate(new Date(newDueDate));
            console.log('Adding new task:', {
                leadId,
                task: newTask,
                dueDate,
                userId: auth.currentUser.uid
            });

            const addTaskRequest: AddTaskRequest = {
                leadId,
                task: newTask.trim(),
                dueDate,
            };

            await addTask(db, auth.currentUser, addTaskRequest);

            await fetchTasksList();

            setNewTask("");
            setNewDueDate("");
            setShowAddTask(false);
            setToastMessage('Task added successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            setToastMessage('Error in adding task. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error adding task:', error);
        }
    };

    const handleMarkAsDone = async (taskId: string) => {
        if (!leadId) return;

        try {
            await updateTask(db, auth.currentUser, {
                leadId,
                taskId,
                isDone: true
            });
            fetchTasksList();
            setToastMessage('Marked as done !');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            setToastMessage('Error to mark as done. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error updating task status:', error);
        }
    };

    const formatDate = (timestamp: Timestamp) => {
        if (!timestamp || !timestamp.toDate) return '';
        const date = timestamp.toDate();
        return date.toLocaleDateString();
    };

    const getDaysStatus = (dueDate: Timestamp, isDone: boolean) => {
        if (isDone) {
            return { text: "Done", isDone: true, isOverdue: false };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDateObj = dueDate.toDate();
        dueDateObj.setHours(0, 0, 0, 0);

        const diffTime = dueDateObj.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { text: "Overdue", isDone: false, isOverdue: true };
        } else if (diffDays === 0) {
            return { text: "Due Today", isDone: false, isOverdue: false, isDueToday: true };
        } else if (diffDays === 1) {
            return { text: "1 day to go", isDone: false, isOverdue: false, isDueToday: false };
        } else {
            return { text: `${diffDays} days to go`, isDone: false, isOverdue: false, isDueToday: false };
        }
    };

    return (
        <div>
            <div className="mt-8 mx-12">
                <div className="flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-gray-800">Student Tasks:</h1>
                    {write && (
                        <button
                            onClick={() => setShowAddTask(true)}
                            className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                            style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                        >
                            <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px' }}>
                                Add Task
                            </span>
                            <img src="/images/desktop/staffadd.svg" alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                        </button>
                    )}
                </div>

                <div className="space-y-3 mt-6">
                    {/* Incomplete Tasks */}
                    {tasks.filter(task => !task.isDone).length > 0 && (
                        <>
                            <div className="mb-3">
                                <h2 className="text-sm font-medium text-gray-500">Pending Tasks</h2>
                            </div>
                            <div className="space-y-3">
                                {tasks
                                    .filter(task => !task.isDone)
                                    .map((task) => (
                                        <div key={task.id} className="flex items-center justify-between bg-gray-100 p-3 rounded-3xl">
                                            <div className="flex-1">
                                                <p className="ml-2 text-sm font-medium text-gray-800">{task.task}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="ml-2 text-xs text-gray-500">
                                                        Due: {formatDate(task.dueDate)}
                                                    </span>
                                                    <span
                                                        className={
                                                            getDaysStatus(task.dueDate, task.isDone).isDone
                                                                ? 'text-xs font-medium text-green-600'
                                                                : getDaysStatus(task.dueDate, task.isDone).isOverdue
                                                                    ? 'text-xs font-medium text-red-600'
                                                                    : getDaysStatus(task.dueDate, task.isDone).isDueToday
                                                                        ? 'text-xs font-medium text-orange-500'
                                                                        : 'text-xs font-medium'
                                                        }
                                                        style={
                                                            !getDaysStatus(task.dueDate, task.isDone).isDone &&
                                                                !getDaysStatus(task.dueDate, task.isDone).isOverdue &&
                                                                !getDaysStatus(task.dueDate, task.isDone).isDueToday
                                                                ? { color: '#02385E' }
                                                                : undefined
                                                        }
                                                    >
                                                        {getDaysStatus(task.dueDate, task.isDone).text}
                                                    </span>
                                                </div>
                                            </div>
                                            {write && (
                                                <button
                                                    onClick={() => handleMarkAsDone(task.id)}
                                                    className="px-3 py-1 text-sm text-white rounded-full bg-[#02385E] hover:bg-[#02385E]/90"
                                                >
                                                    Mark as Done
                                                </button>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}

                    {/* Completed Tasks */}
                    {tasks.filter(task => task.isDone).length > 0 && (
                        <>
                            <div className="mt-6 mb-3">
                                <h2 className="text-sm font-medium text-gray-500">Completed Tasks</h2>
                            </div>
                            <div className="space-y-3">
                                {tasks
                                    .filter(task => task.isDone)
                                    .map((task) => (
                                        <div key={task.id} className="flex items-center justify-between bg-gray-100 p-3 rounded-3xl opacity-70">
                                            <div className="flex-1">
                                                <p className="ml-2 text-sm font-medium text-gray-800">{task.task}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="ml-2 text-xs text-gray-500">
                                                        Due: {formatDate(task.dueDate)}
                                                    </span>
                                                    <span className="text-xs font-medium text-green-600">
                                                        {getDaysStatus(task.dueDate, task.isDone).text}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}

                    {tasks.length === 0 && (
                        <div className="bg-gray-100 p-3 rounded-3xl">
                            <p className="text-sm text-gray-500">No tasks available</p>
                        </div>
                    )}
                </div>

                <hr
                    className="mt-10 w-3/4"
                    style={{
                        border: "none",
                        height: "2px",
                        background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)",
                    }}
                />
            </div>

            {showAddTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-montserrat text-gray-900">Add New Task</h2>
                            <button onClick={() => setShowAddTask(false)}>
                                <img
                                    src="/images/desktop/close.svg"
                                    alt="Close"
                                    className="w-6 h-6"
                                />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Task Description</label>
                                <textarea
                                    value={newTask}
                                    onChange={(e) => setNewTask(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                                <input
                                    type="datetime-local"
                                    value={newDueDate}
                                    onChange={(e) => setNewDueDate(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex justify-end gap-4 mt-6">


                                <button
                                    onClick={() => setShowAddTask(false)}
                                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-600"
                                >
                                    Cancel
                                </button>
{/*  */}
                                <div>
                                    <GradientSubmitButton
                                        onClick={handleAddTask}
                                        className="bg-gradient-to-b from-[#3483BE] to-[#02385E] hover:from-[#4C95CE] hover:to-[#03517E] px-4 py-2 text-white rounded-full"
                                    >
                                         
                                    </GradientSubmitButton>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default Tasks;